import { InfoSectionData } from '../field/FieldInfoButton';

export const mapEditorInfo: InfoSectionData[] = [
  {
    title: 'Overview',
    description: `
      The <strong>Map Editor</strong> allows you to create visual representations of your playing field.
      <br/><br/>
      You can draw structures, place objects, mark zones, and position nodes on an interactive canvas.
      <br/><br/>
      When you save, the map is automatically exported as an image for display on your field page.
    `,
  },
  {
    title: 'Editor Modes',
    fields: [
      {
        name: 'Schema',
        description: 'Draw and place visual elements on the map. Use the <strong>Template Library</strong> on the right to add buildings, barriers, cover, zones, and markers.',
      },
      {
        name: 'Nodes',
        description: 'Position your physical <strong>LoRa nodes</strong> on the map. Drag nodes from the <strong>Node Positions</strong> panel to their real-world locations.',
      },
      {
        name: 'Mission Templates',
        description: 'Create and manage <strong>mission templates</strong> with pre-configured device positions. Save device layouts for quick mission setup.',
      },
    ],
  },
  {
    title: 'Template Library',
    description: `
      The Template Library contains pre-made elements organized into categories:
      <br/><br/>
      <strong>Structures</strong> - Buildings, rooms, walls, doors<br/>
      <strong>Barriers</strong> - Fences, barricades, sandbags, wire, roads<br/>
      <strong>Cover</strong> - Trees, bushes, crates, vehicles, rocks<br/>
      <strong>Zones</strong> - Spawn areas, objective zones, safe/danger zones<br/>
      <strong>Markers</strong> - Waypoints, targets, flags, bombs, exits
    `,
  },
  {
    title: 'Working with Elements',
    fields: [
      {
        name: 'Adding Elements',
        description: 'Click a template in the library, then click on the canvas to place it. The element will appear at the clicked position.',
      },
      {
        name: 'Selecting & Moving',
        description: 'Click any element to select it. Drag to move. Use the <strong>selection handles</strong> to resize or rotate.',
      },
      {
        name: 'Rotating Elements',
        description: 'When an element is selected, drag the <strong>rotation handle</strong> (circle above the element) to rotate it. Elements snap to 45-degree angles.',
      },
      {
        name: 'Deleting Elements',
        description: 'Select an element and press <strong>Delete</strong> or <strong>Backspace</strong> to remove it.',
      },
    ],
  },
  {
    title: 'Drawing Tools',
    fields: [
      {
        name: 'Line Tool',
        description: 'Draw straight lines between two points. Click to start, click again to end the line.',
      },
      {
        name: 'Border Tool',
        description: 'Draw the map boundary. Click and drag to create a border line that defines the playable area.',
      },
    ],
  },
  {
    title: 'Toolbar Controls',
    fields: [
      {
        name: 'Save',
        description: 'Saves all elements and automatically exports the map as an image. The image will be displayed on your field page.',
      },
      {
        name: 'Undo / Redo',
        description: 'Undo the last action or redo a previously undone action. Keyboard shortcuts: <strong>Ctrl+Z</strong> / <strong>Ctrl+Y</strong>.',
      },
      {
        name: 'Grid Toggle',
        description: 'Show or hide the background grid. Useful for aligning elements.',
      },
      {
        name: 'Snap to Grid',
        description: 'When enabled, elements will snap to grid intersections for precise positioning.',
      },
      {
        name: 'Background Toggle',
        description: 'Switch between dark and white canvas background. Choose what looks best for your map.',
      },
    ],
  },
  {
    title: 'Mission Templates',
    description: `
      <strong>Mission Templates</strong> allow you to pre-configure device positions for different game scenarios:
      <br/><br/>
      <strong>What are Mission Templates?</strong><br/>
      Each template saves the positions of all devices (bombs, flags, terminals, sensors) for a specific mission type.
      When starting a new game, referees can select a template to instantly place all devices in their designated positions.
      <br/><br/>
      <strong>How to create a template:</strong><br/>
      1. Switch to <strong>Mission Templates</strong> mode<br/>
      2. Select an <strong>Operation</strong> from the dropdown (based on your purchased device sets)<br/>
      3. Click <strong>Create Template</strong> and give it a name<br/>
      4. Drag devices from the panel onto the map<br/>
      5. Position each device where it should be placed during gameplay<br/>
      6. Click <strong>Save Template</strong> to save positions
      <br/><br/>
      <strong>Benefits:</strong><br/>
      • Quick game setup - no need to manually position devices each time<br/>
      • Consistent gameplay - same device positions for fair matches<br/>
      • Multiple scenarios - create different templates for various game modes<br/>
      • Visual preview - players can see device locations on the public map page
    `,
  },
  {
    title: 'Node Positioning',
    description: `
      In <strong>Nodes mode</strong>, you can position your physical nodes on the map:
      <br/><br/>
      1. Select a node from the <strong>Node Positions</strong> panel<br/>
      2. Click on the canvas where the node is physically located<br/>
      3. Adjust the <strong>scale</strong> slider to change the icon size<br/>
      4. Use the <strong>lock</strong> button to prevent accidental changes
      <br/><br/>
      Node positions help referees set up games and track node locations.
    `,
  },
  {
    title: 'Tips & Best Practices',
    description: `
      <strong>Start with structures</strong> - Add major buildings and barriers first<br/>
      <strong>Use zones for spawn areas</strong> - Mark team spawns clearly with colored zones<br/>
      <strong>Position nodes accurately</strong> - Match real-world node locations<br/>
      <strong>Save frequently</strong> - Changes are auto-saved, but manual save ensures export<br/>
      <strong>Use white background</strong> for printing, dark for digital display
    `,
  },
];
